/**
 * Labor only: any package, with the customer buying the materials.
 *
 * Jonathon, 2026-09-30: "there needs to be a labor only template. check out
 * giovanny adan for example." Job 1084 was built by hand: two shingle packages
 * generated as usual, the material orders left off, and "This scope of work is
 * for LABOR ONLY" typed over the top of each scope. Asked how it should work,
 * he chose a tick on any template rather than a template of its own, because
 * the warranty "depends on the package presented". ADR 0240.
 *
 * So labor only is an answer, like a flat roof's build or a metal roof's
 * finish, and it is kept with them in `generation_answers`. What it changes:
 *
 *   - no line that would go on a material order is generated, so no material
 *     order is written. The work order is untouched, dump trailer and decking
 *     labour included — his answer: "Dump trailer + 3 sheets decking labor";
 *   - the scope opens with his own sentence;
 *   - the estimate is named for it, so the picker says which option is which.
 *
 * The package's wording, warranty and headline stay as the template has them,
 * except the wood bonus. Its promise of free decking is a promise of materials
 * the customer is now buying, so Jonathon, 2026-10-01: "That bonus should not
 * print" (ADR 0255).
 */

import { targetForCategory } from "./roofSlots.js";

/** Jonathon's sentence, from job 1084, word for word. */
export const LABOR_ONLY_SCOPE_HEADING = "This scope of work is for LABOR ONLY";

/** The template's scope with the labor-only sentence above it. */
export function laborOnlyScope(scope: string | null | undefined): string {
  const body = (scope ?? "").trim();
  return body
    ? `${LABOR_ONLY_SCOPE_HEADING}\n\n${body}`
    : LABOR_ONLY_SCOPE_HEADING;
}

/**
 * Whether a wording line is a wood bonus: "Worry Free Wood Bonus", "Peace of
 * Mind Wood Preventative Bonus - $1550 Value!", "Essentials Wood Bonus - $425
 * Value". The same promise at three sizes, read by its proposal heading
 * because the heading is the customer's words and the product name is the
 * office's to rename.
 */
export function isWoodBonus(section: string | null | undefined): boolean {
  return /\bwood\b.*\bbonus\b/i.test(section ?? "");
}

/** "Labor Only - Standard Architectural Shingles". */
export function laborOnlyName(name: string): string {
  return `Labor Only - ${name}`;
}

/**
 * Whether a package's own words say it is labor only: its name, or any line
 * the customer reads. Both kinds say so — the tick on any template opens the
 * scope with `LABOR_ONLY_SCOPE_HEADING` and names the estimate with
 * `laborOnlyName`, and the New Construction Labor Only template is named for
 * it and says "This is a labor only scope".
 *
 * Read from the words because they are what reaches a built document, from
 * the rep's screen or anywhere else. A false yes keeps a package on the
 * template's own warranty words, which is the safe side to be wrong on.
 */
export function isLaborOnlyPackage(pkg: {
  name?: string | null | undefined;
  blocks?:
    | readonly {
        lines: readonly { name: string; body: string | null }[];
      }[]
    | null
    | undefined;
}): boolean {
  const says = (text: string | null | undefined) =>
    /\blabou?r[\s-]+only\b/i.test(text ?? "");
  return (
    says(pkg.name) ||
    (pkg.blocks ?? []).some((block) =>
      block.lines.some((line) => says(line.name) || says(line.body)),
    )
  );
}

/**
 * Whether a product would be bought from a supplier. The same routing the
 * generator uses to choose between the material order and the work order, so
 * labor only drops exactly what would have been ordered.
 */
export function isBoughtIn(product: {
  category?: string | null;
  kind?: string | null;
}): boolean {
  return targetForCategory(product.category, product.kind) === "material_order";
}
